import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categoryLinks } from "@/mocks/siteData";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50 border-b border-background-200/70"
          : "bg-gradient-to-b from-black/55 via-black/30 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[70px]">
          <Link
            to="/"
            onClick={closeAll}
            className="text-xl md:text-2xl font-heading font-bold whitespace-nowrap text-white drop-shadow-lg"
          >
            superbuy.wtf
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              Home
            </Link>

            <Link
              to="/articles/superbuy-spreadsheet-shopping-guide"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              Shopping Guide
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "bg-background-100 text-foreground-800 hover:bg-background-200"
                    : "bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm"
                }`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 z-50 max-h-[420px] overflow-y-auto">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-foreground-950 text-background-50 hover:bg-foreground-800"
                  : "bg-white text-foreground-950 hover:bg-white/90"
              }`}
            >
              superbuy spreadsheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-accent-500 text-foreground-950 hover:bg-accent-600"
              }`}
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 rounded-md flex items-center justify-center transition-all cursor-pointer ${
              scrolled
                ? "bg-background-100 text-foreground-800"
                : "bg-white/15 text-white hover:bg-white/25"
            }`}
            aria-label="Toggle menu"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`text-lg ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </span>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 pb-4 pt-2">
            <Link
              to="/"
              onClick={closeAll}
              className="block px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-md transition-colors"
            >
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-home-4-line"></i>
              </span>
              Home
            </Link>

            <Link
              to="/articles/superbuy-spreadsheet-shopping-guide"
              onClick={closeAll}
              className="block px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-md transition-colors"
            >
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-book-open-line"></i>
              </span>
              Shopping Guide
            </Link>

            <div className="px-4 py-2 mt-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-1.5 px-2 py-2 rounded-md text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    onClick={closeAll}
                  >
                    <span className="w-3 h-3 rounded-full bg-accent-100 flex-shrink-0"></span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 px-4 mt-3 pt-3 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-foreground-950 text-background-50 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
                onClick={closeAll}
              >
                superbuy spreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-500 text-foreground-950 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer"
                onClick={closeAll}
              >
                buy it now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}